import {useState} from 'react'
import {AnchoredOverlay, BranchName, Button, NavList} from '@primer/react'
import {ArrowRightIcon, StackIcon} from '@primer/octicons-react'
import {getBaseBranchText} from '../utils/branch-label'
import type {StackData} from '../page-data/payloads/header'
import {PullRequestBranchName} from './PullRequestBranchName'
import {StateIcon} from './StateIcon'
import {branchPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

type StackStateProps = {
  pullRequestNumber: number
  stack: StackData | undefined | null
}

export function StackState({pullRequestNumber, stack}: StackStateProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isPullRequestStacksEnabled = useFeatureFlag('pull_request_stacks')
  const [open, setOpen] = useState<boolean>(false)

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const {baseBranch, repositoryOwnerLogin, repositoryName, position, size, pulls} = stack
  const baseBranchText = getBaseBranchText(baseBranch, repositoryOwnerLogin, repositoryOwnerLogin)

  return (
    <div className="fgColor-muted d-flex flex-items-center">
      <AnchoredOverlay
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderAnchor={props => (
          <Button
            className="d-inline"
            variant="link"
            leadingVisual={StackIcon}
            aria-label={`Navigate pull request stack, position ${position} of ${size}`}
            {...props}
          >
            {position}/{size}
          </Button>
        )}
      >
        <NavList className="m-1">
          {pulls.map(stackedPR => (
            <NavList.Item
              key={stackedPR.number}
              href={stackedPR.url}
              aria-current={stackedPR.number === pullRequestNumber ? 'page' : undefined}
            >
              <StateIcon state={stackedPR.state} /> {stackedPR.title}{' '}
              <span className="fgColor-muted">#{stackedPR.number}</span>
            </NavList.Item>
          ))}
          <NavList.Item href={branchPath({owner: repositoryOwnerLogin, repo: repositoryName, branch: baseBranch})}>
            <BranchName as="span">{baseBranchText}</BranchName>
          </NavList.Item>
        </NavList>
      </AnchoredOverlay>
      <ArrowRightIcon verticalAlign="middle" size={16} />{' '}
      <PullRequestBranchName
        branch={baseBranch}
        branchText={baseBranchText}
        repositoryName={repositoryName}
        repositoryOwner={repositoryOwnerLogin}
      />
    </div>
  )
}
